import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Clock, MapPin, Navigation, Mail, ArrowRight, CheckCircle2, Loader2, Map as MapIcon, AlertTriangle, Share2, Copy, MessageCircle, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import useSEO from "@/lib/seo";
import Time24Picker from "@/components/Time24Picker";

const WEEKDAYS = [
  { value: 1, short: "Mo", long: "Montag" },
  { value: 2, short: "Di", long: "Dienstag" },
  { value: 3, short: "Mi", long: "Mittwoch" },
  { value: 4, short: "Do", long: "Donnerstag" },
  { value: 5, short: "Fr", long: "Freitag" },
  { value: 6, short: "Sa", long: "Samstag" },
  { value: 7, short: "So", long: "Sonntag" },
];

function AddressAutocomplete({ label, value, onChange, onSelect, testid, placeholder }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const userTypedRef = useRef(false);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!value || value.length < 3) { setResults([]); return; }
    // Only fetch+open if the change came from user typing (not programmatic prefill/select).
    if (!userTypedRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/commuter/autocomplete`, { params: { q: value, limit: 6 } });
        setResults(r.data?.results || []);
        if (document.activeElement === inputRef.current) setOpen(true);
      } catch { /* silent */ }
      setLoading(false);
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value]);

  return (
    <div ref={wrapRef} className="relative">
      <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">{label}</label>
      <div className="relative">
        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => { userTypedRef.current = true; onChange(e.target.value); }}
          onFocus={() => results.length > 0 && setOpen(true)}
          onBlur={() => { userTypedRef.current = false; }}
          placeholder={placeholder}
          autoComplete="off"
          data-testid={testid}
          className="w-full bg-white border border-brand-line pl-10 pr-3 py-3 text-sm text-neutral-900 focus:outline-none focus:border-[hsl(var(--brand-green-dark))] transition-colors"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-brand-line shadow-lg max-h-64 overflow-auto" data-testid={`${testid}-results`}>
          {results.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); userTypedRef.current = false; onSelect(r); setOpen(false); }}
                data-testid={`${testid}-result-${i}`}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-[hsl(var(--brand-soft))] border-b border-neutral-100 last:border-b-0"
              >
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ShareRouteButton({ start, dest, departure }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const popRef = useRef(null);

  const shareUrl = (() => {
    const u = new URL("/berufsverkehr-ticker", window.location.origin);
    if (start) u.searchParams.set("start", start);
    if (dest) u.searchParams.set("dest", dest);
    if (departure) u.searchParams.set("t", departure);
    return u.toString();
  })();
  const shareText = `Schau mal: meine Strecke ${start} → ${dest} im Berufsverkehr-Ticker Detmold`;

  useEffect(() => {
    const onDoc = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const tryNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Berufsverkehr-Ticker Detmold", text: shareText, url: shareUrl });
        return true;
      } catch { /* user dismissed → fall through to popover */ }
    }
    return false;
  };

  const onClick = async (e) => {
    e.preventDefault();
    const ok = await tryNative();
    if (!ok) setOpen((v) => !v);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div ref={popRef} className="relative inline-block">
      <button
        type="button"
        onClick={onClick}
        data-testid="commuter-share-button"
        className="inline-flex items-center gap-2 border border-brand-line bg-white px-4 py-2.5 text-sm font-medium text-neutral-800 hover:bg-neutral-50 transition-colors"
      >
        <Share2 className="h-4 w-4" /> Strecke teilen
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-40 w-64 bg-white border border-brand-line shadow-lg p-2" data-testid="commuter-share-popover">
          <a
            href={`https://wa.me/?text=${encodeURIComponent(shareText + "\n" + shareUrl)}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="commuter-share-whatsapp"
            className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-[hsl(var(--brand-soft))] text-neutral-800"
            onClick={() => setOpen(false)}
          >
            <MessageCircle className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" /> Per WhatsApp teilen
          </a>
          <a
            href={`mailto:?subject=${encodeURIComponent("Berufsverkehr-Ticker Detmold")}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`}
            data-testid="commuter-share-email"
            className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-[hsl(var(--brand-soft))] text-neutral-800"
            onClick={() => setOpen(false)}
          >
            <Mail className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" /> Per E-Mail teilen
          </a>
          <button
            type="button"
            onClick={copyLink}
            data-testid="commuter-share-copy"
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-[hsl(var(--brand-soft))] text-neutral-800 text-left"
          >
            {copied ? <Check className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" /> : <Copy className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />}
            {copied ? "Link kopiert!" : "Link kopieren"}
          </button>
        </div>
      )}
    </div>
  );
}

function fmtMin(seconds) {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} Min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${String(r).padStart(2, "0")} Min` : `${h} h`;
}

function SeverityBadge({ severity }) {
  const palette = {
    green:  ["bg-[hsl(var(--brand-soft))]", "text-[hsl(var(--brand-green-dark))]", "Freie Fahrt"],
    yellow: ["bg-amber-50", "text-amber-800", "Verzögerung erwartet"],
    red:    ["bg-red-50", "text-red-800", "Starke Behinderung"],
  };
  const [bg, fg, label] = palette[severity] || palette.green;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.12em] ${bg} ${fg}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

function RoutePreviewCard({ preview, start, dest, departure }) {
  const delaySec = Math.max(0, (preview.duration_traffic_s || 0) - (preview.duration_freeflow_s || 0));
  return (
    <div className="mt-3 border border-brand-line bg-[hsl(var(--brand-soft))]/30 overflow-hidden" data-testid="commuter-preview-card">
      {preview.static_map_url && (
        <img
          src={preview.static_map_url}
          alt="Streckenübersicht"
          className="w-full h-auto block border-b border-brand-line"
          loading="lazy"
        />
      )}
      <div className="p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SeverityBadge severity={preview.severity || "green"} />
          <div className="flex items-center gap-3">
            {preview.provider && (
              <span className="text-[11px] text-neutral-500 uppercase tracking-[0.1em] hidden sm:inline">Daten: {preview.provider === "tomtom" ? "TomTom Live-Verkehr" : "Schätzwert"}</span>
            )}
            <ShareRouteButton start={start} dest={dest} departure={departure} />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.1em] text-neutral-500 mb-1">Fahrzeit</div>
            <div className="text-xl font-semibold text-neutral-900" data-testid="commuter-preview-duration">{fmtMin(preview.duration_traffic_s || 0)}</div>
            {delaySec > 60 && (
              <div className="text-xs text-red-700 mt-1">+ {fmtMin(delaySec)} Mehrzeit ggü. freier Fahrt</div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.1em] text-neutral-500 mb-1">Distanz</div>
            <div className="text-xl font-semibold text-neutral-900">{((preview.distance_m || 0) / 1000).toFixed(1)} km</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.1em] text-neutral-500 mb-1">Verbindung</div>
            <div className="text-sm text-neutral-700 leading-snug">{preview.summary || "Schnellste Route"}</div>
          </div>
        </div>

        {(preview.warnings?.length > 0 || preview.sperrung_warnings?.length > 0) && (
          <div className="border border-amber-300 bg-amber-50 px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-amber-900 mb-2">
              <AlertTriangle className="h-4 w-4" /> Hinweise auf der Strecke
            </div>
            <ul className="list-disc pl-5 text-sm text-neutral-800 space-y-1" data-testid="commuter-preview-warnings">
              {preview.warnings?.map((w, i) => <li key={`w${i}`}>{w.description}</li>)}
              {preview.sperrung_warnings?.map((s, i) => <li key={`s${i}`}>{s}</li>)}
            </ul>
          </div>
        )}

        {preview.alternative && (
          <div
            className={`border px-4 py-3 ${
              preview.comparison?.recommend_alt
                ? "bg-[hsl(var(--brand-soft))]/60 border-[hsl(var(--brand-green))]"
                : "bg-neutral-50 border-brand-line"
            }`}
            data-testid="commuter-preview-alternative"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[hsl(var(--brand-green-dark))]">
                {preview.comparison?.recommend_alt
                  ? `Empfohlene Alternativroute · ${fmtMin(preview.comparison.saving_s)} schneller`
                  : "Alternative Verbindung"}
              </div>
            </div>
            {preview.alternative.summary && (
              <p className="text-sm text-neutral-700 leading-relaxed mb-1.5">
                Über <strong className="text-neutral-900">{preview.alternative.summary}</strong>
              </p>
            )}
            <div className="text-xs text-neutral-600 flex flex-wrap gap-x-4 gap-y-1">
              <span>Fahrzeit: <strong className="text-neutral-900">{fmtMin(preview.alternative.duration_traffic_s)}</strong></span>
              <span>Distanz: <strong className="text-neutral-900">{(preview.alternative.distance_m / 1000).toFixed(1)} km</strong></span>
              {preview.comparison?.saving_s > 0 && (
                <span className="text-[hsl(var(--brand-green-dark))] font-medium">Ersparnis: {fmtMin(preview.comparison.saving_s)}</span>
              )}
            </div>
          </div>
        )}

        {preview.comparison?.recommendation_text && (
          <div
            className={`border px-4 py-3 ${
              preview.comparison.recommend_alt
                ? "bg-[hsl(var(--brand-green-dark))] text-white border-[hsl(var(--brand-green-dark))]"
                : "bg-white text-neutral-800 border-brand-line"
            }`}
            data-testid="commuter-preview-recommendation"
          >
            <div className={`text-[10px] uppercase tracking-[0.12em] mb-1 ${preview.comparison.recommend_alt ? "opacity-70" : "text-neutral-500"}`}>
              Kurzempfehlung
            </div>
            <p className="text-sm leading-snug">{preview.comparison.recommendation_text}</p>
          </div>
        )}

        <p className="text-xs text-neutral-500 leading-relaxed">
          Diese Vorschau wird einmalig berechnet und nicht gespeichert. Melden Sie sich unten an, um diese Auswertung an Ihren gewählten Tagen automatisch 10 Minuten vor Abfahrt per E-Mail zu erhalten.
        </p>
      </div>
    </div>
  );
}

export default function BerufsverkehrTicker() {
  useSEO({
    title: "Berufsverkehr-Ticker — persönliche Routenempfehlung jeden Morgen",
    description:
      "Erhalten Sie jeden Morgen 10 Minuten vor Abfahrt Ihre persönliche Routenempfehlung mit Live-Verkehrsdaten für Detmold und Umgebung — kostenfrei und mit Doppel-Opt-In.",
    path: "/berufsverkehr-ticker",
  });

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    start_address: "",
    destination_address: "",
    departure_time: "07:30",
    weekdays: [1, 2, 3, 4, 5],
    consent_privacy: false,
    consent_auto_routing: false,
    website: "", // honeypot — must stay empty
  });
  const [formLoadedAt] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // Strecken-Vorschau
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [preview, setPreview] = useState(null);

  // Deep-link prefill: ?start=...&dest=...&t=07:30 (used by share-Links).
  const [searchParams] = useSearchParams();
  const prefillRan = useRef(false);
  useEffect(() => {
    if (prefillRan.current) return;
    const s = searchParams.get("start");
    const d = searchParams.get("dest");
    const t = searchParams.get("t");
    if (!s && !d && !t) return;
    prefillRan.current = true;
    setForm((f) => ({
      ...f,
      ...(s ? { start_address: s } : {}),
      ...(d ? { destination_address: d } : {}),
      ...(t && /^([01]\d|2[0-3]):[0-5]\d$/.test(t) ? { departure_time: t } : {}),
    }));
  }, [searchParams]);

  const previewRoute = async () => {
    setPreviewError("");
    setPreview(null);
    if (!form.start_address.trim() || !form.destination_address.trim()) {
      setPreviewError("Bitte tragen Sie zuerst Start- und Zieladresse ein.");
      return;
    }
    setPreviewLoading(true);
    try {
      const r = await api.post("/commuter/preview-route", {
        start_address: form.start_address,
        destination_address: form.destination_address,
        departure_time: form.departure_time,
      });
      setPreview(r.data);
    } catch (e) {
      setPreviewError(formatApiError(e.response?.data?.detail) || "Vorschau fehlgeschlagen.");
    }
    setPreviewLoading(false);
  };

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d].sort(),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent_privacy || !form.consent_auto_routing) {
      setError("Bitte erteilen Sie beide Zustimmungen, um den Service zu aktivieren.");
      return;
    }
    if (form.weekdays.length === 0) {
      setError("Bitte wählen Sie mindestens einen Wochentag.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/commuter/subscribe", { ...form, form_loaded_at: formLoadedAt });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Anmeldung fehlgeschlagen.");
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div data-testid="commuter-success-page">
        <section className="relative bg-neutral-50 overflow-hidden">
          <div className="container-narrow pt-16 lg:pt-24 pb-16 lg:pb-24 relative z-10 max-w-3xl">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white border-2 border-[hsl(var(--brand-green))] mb-6">
              <Mail className="h-7 w-7 text-[hsl(var(--brand-green-strong))]" strokeWidth={1.8} />
            </div>
            <h1 className="font-semibold text-4xl sm:text-5xl leading-[1.1] mb-5">
              Fast geschafft — bitte E-Mail bestätigen
            </h1>
            <p className="text-lg text-neutral-700 leading-relaxed max-w-2xl mb-3">
              Wir haben Ihnen soeben eine Bestätigungs-E-Mail an <strong className="text-neutral-900">{form.email}</strong> gesendet. Bitte klicken Sie auf den enthaltenen Link, um Ihren Berufsverkehr-Ticker zu aktivieren.
            </p>
            <p className="text-sm text-neutral-500 mb-8">
              Der Link ist 48 Stunden gültig. Sollten Sie die E-Mail nicht innerhalb weniger Minuten erhalten, prüfen Sie bitte Ihren Spam-Ordner.
            </p>
            <Link
              to="/"
              data-testid="commuter-back-home"
              className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white pl-6 pr-5 py-3.5 rounded-full hover:bg-[hsl(var(--brand-green-strong))] transition-colors text-sm font-medium"
            >
              Zur Startseite <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="commuter-form-page">
      {/* Hero */}
      <section className="relative bg-[hsl(var(--brand-soft))] overflow-hidden">
        <div className="container-narrow pt-16 lg:pt-20 pb-12 lg:pb-16 relative z-10 max-w-4xl">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-green))]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-dark))]">Berufsverkehr-Ticker · Detmold</span>
          </div>
          <h1 className="font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
            Jeden Morgen <em className="not-italic text-[hsl(var(--brand-green-strong))]">die schnellste Route</em><br/>
            — direkt in Ihr Postfach.
          </h1>
          <p className="text-lg text-neutral-700 leading-relaxed max-w-2xl">
            Tragen Sie Ihre Strecke einmalig ein und erhalten Sie an Ihren gewählten Tagen <strong>genau 10 Minuten vor Abfahrt</strong> eine persönliche Routenempfehlung mit aktuellen Verkehrsdaten, Verzögerungen und Sperrungen in Detmold und Umgebung. Kostenfrei, jederzeit kündbar.
          </p>
        </div>
      </section>

      {/* Wie funktioniert es */}
      <section className="border-b border-brand-line">
        <div className="container-narrow py-12 max-w-5xl grid sm:grid-cols-3 gap-6">
          {[
            { icon: MapPin, t: "Einmal einrichten", d: "Start- und Zieladresse, Abfahrtszeit und Wochentage angeben — fertig." },
            { icon: Navigation, t: "TomTom-Live-Daten", d: "Wir berechnen jeden Morgen die schnellste Route mit aktuellen Verkehrsdaten und Sperrungen." },
            { icon: Clock, t: "10 Min. vor Abfahrt", d: "Pünktlich zur richtigen Zeit landet Ihre persönliche Empfehlung in Ihrem Postfach." },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-brand-line p-6">
              <s.icon className="h-5 w-5 text-[hsl(var(--brand-green-dark))] mb-4" />
              <p className="font-medium text-neutral-900 mb-1">{s.t}</p>
              <p className="text-sm text-neutral-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Formular */}
      <section>
        <div className="container-narrow py-16 max-w-3xl">
          <form onSubmit={submit} className="bg-white border border-brand-line p-6 sm:p-10 space-y-7" data-testid="commuter-form">
            {/* Honeypot — visually hidden, off-screen, aria-hidden so screen readers skip it.
                Bots that fill every field will trigger this. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
              <label htmlFor="website-do-not-fill">Webseite (bitte nicht ausfüllen)</label>
              <input
                type="text"
                id="website-do-not-fill"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-dark))] mb-3">Anmeldung</p>
              <h2 className="text-2xl font-semibold mb-1">Ihre persönliche Strecke</h2>
              <p className="text-sm text-neutral-500">Pflichtfelder · Servicegebiet: max. 35 km um Detmold</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Vorname</label>
                <input
                  required
                  type="text"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="commuter-first-name"
                  className="w-full bg-white border border-brand-line px-3.5 py-3 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-dark))]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Nachname</label>
                <input
                  required
                  type="text"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="commuter-last-name"
                  className="w-full bg-white border border-brand-line px-3.5 py-3 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-dark))]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">E-Mail-Adresse</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="commuter-email"
                placeholder="ihre.adresse@beispiel.de"
                className="w-full bg-white border border-brand-line px-3.5 py-3 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-dark))]"
              />
            </div>

            <AddressAutocomplete
              label="Startadresse"
              value={form.start_address}
              onChange={(v) => setForm((f) => ({ ...f, start_address: v }))}
              onSelect={(r) => setForm((f) => ({ ...f, start_address: r.label }))}
              testid="commuter-start"
              placeholder="z. B. Hornsche Straße 12, 32756 Detmold"
            />

            <AddressAutocomplete
              label="Zieladresse"
              value={form.destination_address}
              onChange={(v) => setForm((f) => ({ ...f, destination_address: v }))}
              onSelect={(r) => setForm((f) => ({ ...f, destination_address: r.label }))}
              testid="commuter-dest"
              placeholder="z. B. Bielefelder Straße 5, 32756 Detmold"
            />

            {/* Strecke testen */}
            <div className="border-t border-dashed border-brand-line pt-5">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <button
                  type="button"
                  onClick={previewRoute}
                  disabled={previewLoading}
                  data-testid="commuter-preview-button"
                  className="inline-flex items-center gap-2 border border-[hsl(var(--brand-green-dark))] text-[hsl(var(--brand-green-dark))] bg-white px-4 py-2.5 text-sm font-medium hover:bg-[hsl(var(--brand-soft))] transition-colors disabled:opacity-60"
                >
                  {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapIcon className="h-4 w-4" />}
                  {previewLoading ? "Berechne Route…" : "Strecke jetzt testen"}
                </button>
                <span className="text-xs text-neutral-500">Live-Fahrzeit + Verkehrslage, ohne Anmeldung</span>
              </div>

              {previewError && (
                <div className="border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-3" data-testid="commuter-preview-error">
                  {previewError}
                </div>
              )}

              {preview && <RoutePreviewCard preview={preview} start={form.start_address} dest={form.destination_address} departure={form.departure_time} />}
            </div>

            <div className="grid sm:grid-cols-[180px,1fr] gap-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Abfahrtszeit</label>
                <Time24Picker
                  value={form.departure_time}
                  onChange={(v) => setForm({ ...form, departure_time: v })}
                  testId="commuter-departure"
                />
                <p className="text-[11px] text-neutral-500 mt-1.5">Mail kommt 10 Min. vorher</p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.14em] text-neutral-500 mb-2">Wochentage</label>
                <div className="flex flex-wrap gap-2" data-testid="commuter-weekdays">
                  {WEEKDAYS.map((d) => {
                    const active = form.weekdays.includes(d.value);
                    return (
                      <button
                        type="button"
                        key={d.value}
                        onClick={() => toggleDay(d.value)}
                        data-testid={`commuter-day-${d.value}`}
                        aria-pressed={active}
                        title={d.long}
                        className={`h-11 w-12 text-sm font-medium border transition-colors ${
                          active
                            ? "bg-[hsl(var(--brand-green-dark))] text-white border-[hsl(var(--brand-green-dark))]"
                            : "bg-white text-neutral-700 border-brand-line hover:border-[hsl(var(--brand-green))]"
                        }`}
                      >
                        {d.short}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-neutral-500 mt-1.5">Standard: Mo–Fr</p>
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t border-neutral-100">
              <label className="flex gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent_privacy}
                  onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })}
                  data-testid="commuter-consent-privacy"
                  className="mt-0.5 h-4 w-4 accent-[hsl(var(--brand-green-dark))]"
                />
                <span className="text-sm text-neutral-700 leading-relaxed">
                  Ich willige in die Verarbeitung meiner Daten gemäß der <Link to="/datenschutz" className="text-[hsl(var(--brand-green-dark))] underline">Datenschutzerklärung</Link> ein. Eine Abmeldung ist jederzeit über den Link in jeder E-Mail oder den Verwaltungslink möglich.
                </span>
              </label>
              <label className="flex gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent_auto_routing}
                  onChange={(e) => setForm({ ...form, consent_auto_routing: e.target.checked })}
                  data-testid="commuter-consent-routing"
                  className="mt-0.5 h-4 w-4 accent-[hsl(var(--brand-green-dark))]"
                />
                <span className="text-sm text-neutral-700 leading-relaxed">
                  Ich bin damit einverstanden, dass meine Start- und Zieladresse an den Routing-Dienstleister TomTom (Niederlande) übermittelt wird, um meine persönliche Routenempfehlung mit aktuellen Verkehrsdaten zu berechnen.
                </span>
              </label>
            </div>

            {error && (
              <div className="border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-3" data-testid="commuter-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="commuter-submit"
              className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white pl-6 pr-5 py-3.5 rounded-full hover:bg-[hsl(var(--brand-green-strong))] transition-colors text-sm font-medium disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {submitting ? "Wird gesendet…" : "Berufsverkehr-Ticker abonnieren"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>

            <p className="text-xs text-neutral-500 leading-relaxed pt-2">
              Nach Absenden erhalten Sie eine Bestätigungs-E-Mail (Double-Opt-In). Erst nach Klick auf den Bestätigungslink wird Ihr Berufsverkehr-Ticker aktiv. Wir senden keine Werbung.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
